<script setup lang="ts">
import { ref } from 'vue'
import { Check, FileType2, HardDrive, LoaderCircle, Sparkles, Trash2, Type, Upload, X } from '@lucide/vue'
import {
  closeFontLibrary,
  fontLibrary,
  platformFonts,
  removeCustomFont,
  removePlatformFont,
  restorePlatformFonts,
  selectFont,
  selectedFontName,
  uploadFonts,
  visiblePlatformFonts,
} from '../font'

const input = ref<HTMLInputElement | null>(null)

function sizeLabel(bytes: number) {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`
}

async function chooseFiles(event: Event) {
  const target = event.target as HTMLInputElement
  await uploadFonts(Array.from(target.files || []))
  target.value = ''
}

async function dropFiles(event: DragEvent) {
  fontLibrary.dragActive = false
  await uploadFonts(Array.from(event.dataTransfer?.files || []))
}
</script>

<template>
  <Teleport to="body">
    <Transition name="font-panel">
      <div v-if="fontLibrary.panelOpen" class="font-backdrop" @click.self="closeFontLibrary">
        <section class="font-panel" role="dialog" aria-modal="true" aria-labelledby="font-panel-title">
          <header class="font-panel-head">
            <div class="font-title-mark"><Type /></div>
            <div><p>TYPE WARDROBE</p><h2 id="font-panel-title">全站字体衣橱</h2></div>
            <button class="font-close" type="button" aria-label="关闭字体窗口" @click="closeFontLibrary"><X /></button>
          </header>

          <div class="font-current"><Sparkles /><span><small>全站当前字体</small><strong>{{ selectedFontName }}</strong></span></div>

          <section class="font-section">
            <div class="font-section-heading">
              <div><b>平台字体</b><small>已内嵌，点击即可切换；删除只影响当前浏览器</small></div>
              <div class="font-section-actions">
                <button v-if="fontLibrary.hiddenPlatformIds.length" type="button" class="font-restore" @click="restorePlatformFonts">恢复平台字体</button>
                <span>{{ visiblePlatformFonts.length }} / {{ platformFonts.length }} 套</span>
              </div>
            </div>
            <div class="platform-font-grid">
              <article
                v-for="item in visiblePlatformFonts"
                :key="item.id"
                :class="{ selected: fontLibrary.selectedId === item.id }"
              >
                <button
                  type="button"
                  class="platform-font-main"
                  :aria-pressed="fontLibrary.selectedId === item.id"
                  :style="{ '--preview-font': `'${item.family}'` }"
                  @click="selectFont(item.id)"
                >
                  <Check v-if="fontLibrary.selectedId === item.id" class="font-selected-check" />
                  <span class="font-sample">{{ item.sample }}</span>
                  <b>{{ item.name }}</b>
                  <small>{{ item.description }}</small>
                </button>
                <button
                  type="button"
                  class="platform-font-delete"
                  :disabled="visiblePlatformFonts.length === 1"
                  :aria-label="`删除平台字体 ${item.name}`"
                  :title="visiblePlatformFonts.length === 1 ? '至少保留一套平台字体' : `删除 ${item.name}`"
                  @click="removePlatformFont(item.id)"
                ><Trash2 /></button>
              </article>
            </div>
          </section>

          <section class="font-section custom-font-section">
            <div class="font-section-heading"><div><b>我的字体</b><small>只保存在当前浏览器，不会上传到平台</small></div><span>{{ fontLibrary.customFonts.length }} / {{ fontLibrary.limits.maxFonts }}</span></div>

            <button
              type="button"
              :class="['font-dropzone', { active: fontLibrary.dragActive }]"
              :disabled="fontLibrary.uploading"
              @click="input?.click()"
              @dragenter.prevent="fontLibrary.dragActive = true"
              @dragover.prevent="fontLibrary.dragActive = true"
              @dragleave.prevent="fontLibrary.dragActive = false"
              @drop.prevent="dropFiles"
            >
              <LoaderCircle v-if="fontLibrary.uploading" class="spin" />
              <Upload v-else />
              <span><b>{{ fontLibrary.uploading ? '正在识别字体…' : '把字体文件拖到这里' }}</b><small>也可以点击选择文件，加入后会立即应用到全站</small></span>
              <i>TTF · OTF · WOFF · WOFF2</i>
            </button>
            <input ref="input" hidden multiple type="file" accept=".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2" @change="chooseFiles" />

            <div v-if="fontLibrary.customFonts.length" class="custom-font-list">
              <article v-for="item in fontLibrary.customFonts" :key="item.id" :class="{ selected: fontLibrary.selectedId === item.id }">
                <button type="button" class="custom-font-main" :style="{ '--preview-font': `'${item.family}'` }" @click="selectFont(item.id)">
                  <span class="custom-font-icon"><FileType2 /></span>
                  <span><b :style="{ fontFamily: `'${item.family}'` }">{{ item.name }}</b><small>{{ sizeLabel(item.sizeBytes) }} · {{ item.originalName }}</small></span>
                  <Check v-if="fontLibrary.selectedId === item.id" />
                </button>
                <button type="button" class="custom-font-delete" :aria-label="`删除字体 ${item.name}`" @click="removeCustomFont(item.id)"><Trash2 /></button>
              </article>
            </div>
            <div v-else class="font-empty"><HardDrive /><p>还没有自定义字体。拖入一套你喜欢的字体，网站会立刻换上新气质。</p></div>
          </section>

          <p v-if="fontLibrary.message" class="font-message success">{{ fontLibrary.message }}</p>
          <p v-if="fontLibrary.error" class="font-message error">{{ fontLibrary.error }}</p>
          <p class="font-storage-note">单套不超过 50 MB，最多保存 8 套；清除浏览器网站数据后，自定义字体也会被删除。</p>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>
